import * as React from 'react';
import debounce from 'lodash/debounce';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import toPairs from 'lodash/toPairs';
import { useResourceFetch } from '@gandi/resource/react';
import { useTranslator, mapTags } from '@gandi/translate';
import { Base, Block, BlockContent, BlockHeading, Help, Text } from '@gandi/ui';
import { Select } from '@gandi/ui-select';
import { PUBLIC_ORGNAME } from '../consts';
import { isUsableInContext } from '../models/isUsableInContext';
import { templateResource } from '../modules/templateResource';

/**
 * meta argument, cf react-select
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const templateBaseQuery = {
  sort_by: 'name'
};
const TemplateSelector = ({
  onChange,
  value,
  context,
  ...blockProps
}) => {
  const {
    __
  } = useTranslator();
  const [fetchProps, setFetchProps] = React.useState(templateBaseQuery);
  const {
    summaries,
    isFetching,
    meta: resourceMeta
  } = useResourceFetch(templateResource,
  // $FlowFixMe[incompatible-call]
  fetchProps);
  const options = React.useMemo(() => toPairs(groupBy((summaries || []
  // XXX: this filter should be done by FetchProps, cf. isUsableInContext
  ).filter(template => !context || isUsableInContext(template.id, context)),
  // $FlowFixMe[sketchy-null-string]
  template => template.orgname || PUBLIC_ORGNAME)).map(([orgname, templates]) => ({
    label: orgname,
    options: templates.map(record => ({
      value: record.id,
      label: record.name
    }))
  })), [summaries, context]);
  const selectedOption = React.useMemo(() => flatMap(options, option => option.options).find(option => option.value === value?.id) ||
  // react-select does not understand undefined as value
  null,
  // eslint does not see the optionnal chaining

  [options, value]);
  const handleChange = (option, meta) => {
    switch (meta.action) {
      case 'select-option':
        onChange(summaries?.find(template => template.id === option.value));
        break;
      case 'clear':
        onChange(undefined);
        break;
      default:
        // nothing to do
        break;
    }
  };
  const onSearch = searchTerms => {
    setFetchProps(current => ({
      ...current,
      '~name': searchTerms.length ? `*${searchTerms}*` : undefined
    }));
  };
  const handleSearch = debounce(onSearch, 500);
  const handleInputChange = (terms, {
    action
  }) => {
    if (action !== 'input-change') {
      return;
    }
    handleSearch(terms);
  };
  return /*#__PURE__*/_jsxs(Block, {
    ...blockProps,
    children: [/*#__PURE__*/_jsx(BlockHeading, {
      children: __('Select a configuration')
    }), /*#__PURE__*/_jsxs(BlockContent, {
      children: [/*#__PURE__*/_jsx(Select, {
        name: "select-template",
        placeholder: __('Select'),
        options: options,
        onInputChange: handleInputChange,
        onChange: handleChange,
        value: selectedOption,
        isLoading: isFetching,
        isClearable: true
      }), (resourceMeta?.totalCount || 0) > 50 && /*#__PURE__*/_jsx(Help, {
        marginTop: "xsmall",
        color: "light",
        size: "small",
        children: __('💡 You have more than 50 templates. Start writing for more suggestions.')
      }), value && /*#__PURE__*/_jsx(Base, {
        tagName: "p",
        marginTop: "small",
        marginBottom: "none",
        children: mapTags(__('Belongs to [[orga: %(organization)s]]', {
          // $FlowFixMe[sketchy-null-string]
          organization: value.orgname || PUBLIC_ORGNAME
        }), ({
          orga
        }) => ({
          orga: /*#__PURE__*/_jsx(Text, {
            weight: "semibold",
            children: orga
          })
        }))
      })]
    })]
  });
};
TemplateSelector.preFetch = ({
  dispatch
}) => {
  const label = templateResource.computeLabel(templateResource.options.fetchPath,
  // $FlowFixMe[incompatible-call] mixed ≠ number
  templateBaseQuery || {});
  return dispatch(templateResource.fetch(templateResource.options.fetchPath, {
    // $FlowFixMe[incompatible-call]
    params: templateBaseQuery,
    label
  }));
};
export default TemplateSelector;